/**
 * Formatting helpers for annual-report figures and person revenue scales.
 */

import type { FinancialMetric, PersonFinancialFootprintBucket } from '../types'

const DA = 'da-DK'

export const FINANCIAL_SCALE_MAX = 5

export const REVENUE_SCALE_THRESHOLDS = {
  small: 10_000_000,
  mid: 50_000_000,
  medium: 100_000_000,
  large: 1_000_000_000,
} as const

export function revenueToScale(revenue: number | null | undefined): number {
  if (revenue == null || revenue < REVENUE_SCALE_THRESHOLDS.small) return 1
  if (revenue < REVENUE_SCALE_THRESHOLDS.mid) return 2
  if (revenue < REVENUE_SCALE_THRESHOLDS.medium) return 3
  if (revenue < REVENUE_SCALE_THRESHOLDS.large) return 4
  return 5
}

export function scaleToLabel(scale: number): string {
  const clamped = Math.max(1, Math.min(FINANCIAL_SCALE_MAX, scale))
  return '·'.repeat(clamped)
}

export function formatScaleFraction(
  scale: number | null | undefined,
  max: number = FINANCIAL_SCALE_MAX,
): string {
  if (scale == null) return '—'
  const clamped = Math.max(1, Math.min(max, scale))
  return `${clamped}/${max}`
}

/** Compact DKK amount for headlines: "45 mio. kr." / "−70.668 kr." */
export function formatFinancialAmount(amount: number): string {
  const negative = amount < 0
  const abs = Math.abs(amount)

  let formatted: string
  if (abs >= 1_000_000_000) {
    const billions = abs / 1_000_000_000
    formatted = new Intl.NumberFormat(DA, {
      maximumFractionDigits: billions >= 10 ? 0 : 1,
      minimumFractionDigits: 0,
    }).format(billions) + ' mia. kr.'
  } else if (abs >= 1_000_000) {
    const millions = abs / 1_000_000
    formatted = new Intl.NumberFormat(DA, {
      maximumFractionDigits: millions >= 100 ? 0 : 1,
      minimumFractionDigits: 0,
    }).format(millions) + ' mio. kr.'
  } else {
    formatted =
      new Intl.NumberFormat(DA, {
        maximumFractionDigits: 0,
      }).format(abs) + ' kr.'
  }

  return negative ? `−${formatted}` : formatted
}

/** Compact amount for company status row: "1,23 mia." / "123,5 mio." / "123.456". */
export function formatKeyFigureAmount(amount: number): string {
  const negative = amount < 0
  const abs = Math.abs(amount)
  const prefix = negative ? '−' : ''

  if (abs >= 1_000_000_000) {
    const value = abs / 1_000_000_000
    return (
      prefix +
      new Intl.NumberFormat(DA, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value) +
      ' mia.'
    )
  }

  if (abs >= 100_000_000) {
    const value = abs / 1_000_000
    return (
      prefix +
      new Intl.NumberFormat(DA, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(value) +
      ' mio.'
    )
  }

  if (abs >= 1_000_000) {
    const value = abs / 1_000_000
    return (
      prefix +
      new Intl.NumberFormat(DA, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value) +
      ' mio.'
    )
  }

  return (
    prefix +
    new Intl.NumberFormat(DA, {
      maximumFractionDigits: 0,
    }).format(abs)
  )
}

/** Signed profit display: "+2,1 mio. kr." / "−150.000 kr." */
export function formatSignedFinancialAmount(amount: number): string {
  if (amount === 0) return '0 kr.'
  const prefix = amount > 0 ? '+' : '−'
  return prefix + formatFinancialAmount(Math.abs(amount))
}

export function formatChangePercent(value: number | null | undefined): string | null {
  if (value == null || Number.isNaN(value)) return null
  const sign = value > 0 ? '+' : ''
  const formatted = new Intl.NumberFormat(DA, {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  }).format(value)
  return `(${sign}${formatted}%)`
}

/** Compact arrow delta for status rows: "(↑3,4%)" / "(↓15,2%)". */
export function formatChangePercentArrow(value: number | null | undefined): string | null {
  if (value == null || Number.isNaN(value)) return null
  const formatted = new Intl.NumberFormat(DA, {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  }).format(Math.abs(value))
  const arrow = value > 0 ? '↑' : value < 0 ? '↓' : '→'
  return `(${arrow}${formatted}%)`
}

export function changePercentTone(value: number | null | undefined): 'positive' | 'negative' | 'neutral' {
  if (value == null || Number.isNaN(value) || Math.abs(value) < 0.05) return 'neutral'
  return value > 0 ? 'positive' : 'negative'
}

const CHANGE_TONE_CLASS: Record<ReturnType<typeof changePercentTone>, string> = {
  positive: 'text-emerald-700',
  negative: 'text-rose-700',
  neutral: 'text-ink-400',
}

export function changePercentClassName(value: number | null | undefined): string {
  return CHANGE_TONE_CLASS[changePercentTone(value)]
}

export function formatMetricLine(
  label: string,
  metric: FinancialMetric,
  signed = false,
): string {
  const amount = signed
    ? formatSignedFinancialAmount(metric.amount)
    : formatFinancialAmount(metric.amount)
  const delta = formatChangePercent(metric.changePercent)
  return `${label} ${metric.year}: ${amount}${delta ? ` ${delta}` : ''}`
}

export type PersonFootprintTooltipKind = 'ejerskab' | 'indflydelse'

export interface PersonFootprintTooltipContent {
  body: string
  note: string | null
}

export function formatPersonFootprintTooltip(
  kind: PersonFootprintTooltipKind,
  personName: string,
  bucket: Pick<PersonFinancialFootprintBucket, 'aggregatedRevenue' | 'companyCount' | 'companiesWithData'>,
): PersonFootprintTooltipContent {
  const name = personName.trim() || 'personen'

  if (bucket.companiesWithData === 0 || bucket.aggregatedRevenue == null) {
    return {
      body:
        kind === 'ejerskab'
          ? `Ingen offentlige regnskabstal for selskaber, som ${name} har ejerskabandele i.`
          : `Ingen offentlige regnskabstal for selskaber, som ${name} bestyrer.`,
      note: null,
    }
  }

  const amount = formatFinancialAmount(bucket.aggregatedRevenue)
  const body =
    kind === 'ejerskab'
      ? `Samlet omsætning i alle selskaber, som ${name} har ejerskabandele i: ${amount}`
      : `Samlet omsætning i alle selskaber, som ${name} bestyrer: ${amount}`

  const note =
    bucket.companiesWithData < bucket.companyCount
      ? `Bemærk: Kun ${bucket.companiesWithData}/${bucket.companyCount} selskaber har offentliggjort regnskabstal.`
      : null

  return { body, note }
}
